import React, { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";

// The crash page (CrashFallback.jsx) loads only once a page has crashed, so
// no page carries it, nor the animation library it uses, before then.
const CrashFallback = lazy(() => import("./CrashFallback"));

// In the crash page's place if it cannot load either, as when the crash was
// itself a download that failed: its words and its reload, unstyled.
const PlainCrash = () => {
  const { t } = useTranslation();
  return (
    <main style={{ padding: "20vh 24px", textAlign: "center" }}>
      <h1>{t("errors.crash.heading")}</h1>
      <p>{t("errors.crash.body")}</p>
      <button type="button" onClick={() => window.location.reload()}>
        {t("errors.crash.retry")}
      </button>
    </main>
  );
};

class CrashFallbackBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error("The crash page did not load:", error);
  }

  render() {
    return this.state.failed ? <PlainCrash /> : this.props.children;
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Surface in the console for local debugging; this is also the hook point
    // for client telemetry (PostHog is loaded globally) without coupling the
    // boundary to the analytics module's import graph.
    // eslint-disable-next-line no-console
    console.error("Unhandled render error:", error, info?.componentStack);
    if (typeof window !== "undefined" && window.posthog?.captureException) {
      try {
        window.posthog.captureException(error);
      } catch (_) {
        /* never let telemetry crash the fallback */
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <CrashFallbackBoundary>
          <Suspense fallback={null}>
            <CrashFallback />
          </Suspense>
        </CrashFallbackBoundary>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
